import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { itinerariesData } from '../../../mocks/itinerary';
import type { ItineraryDay } from '../../../mocks/itinerary';
import { agencyInfo } from '../../../mocks/dashboard';
import DayCard from './components/DayCard';
import PricingSummary from './components/PricingSummary';
import PDFPreview from './components/PDFPreview';

const brand = agencyInfo.brandColor;

const buildEmptyDay = (dayNumber: number, startDate: string): ItineraryDay => {
  const date = new Date(startDate || new Date().toISOString().split('T')[0]);
  date.setDate(date.getDate() + dayNumber - 1);
  return {
    id: `day-${Date.now()}-${dayNumber}`,
    dayNumber,
    date: date.toISOString().split('T')[0],
    location: '',
    accommodation: '',
    services: [],
  };
};

export default function ItineraryBuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  const existingItn = editId ? itinerariesData.find((i) => i.id === editId) : null;

  const [title, setTitle] = useState(existingItn?.title ?? '');
  const [destination, setDestination] = useState(existingItn?.destination ?? '');
  const [startDate, setStartDate] = useState(existingItn?.startDate ?? '');
  const [endDate, setEndDate] = useState(existingItn?.endDate ?? '');
  const [pax, setPax] = useState(existingItn?.pax ?? 2);
  const [currency, setCurrency] = useState(existingItn?.currency ?? 'AED');
  const [linkedBooking, setLinkedBooking] = useState(existingItn?.linkedBooking ?? '');
  const [linkedCustomer, setLinkedCustomer] = useState(existingItn?.linkedCustomer ?? '');
  const [days, setDays] = useState<ItineraryDay[]>(existingItn?.days ?? []);
  const [showPDF, setShowPDF] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingHeader, setEditingHeader] = useState(!existingItn);

  const nights = (() => {
    if (!startDate || !endDate) return 0;
    const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
    return Math.max(0, Math.round(ms / 86400000));
  })();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDays((prev) => {
      const oldIndex = prev.findIndex((d) => d.id === active.id);
      const newIndex = prev.findIndex((d) => d.id === over.id);
      const moved = arrayMove(prev, oldIndex, newIndex);
      return moved.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    });
  }, []);

  const addDay = () => {
    const nextNum = days.length + 1;
    setDays((prev) => [...prev, buildEmptyDay(nextNum, startDate)]);
  };

  const updateDay = useCallback((updated: ItineraryDay) => {
    setDays((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
  }, []);

  const deleteDay = useCallback((id: string) => {
    setDays((prev) => {
      const filtered = prev.filter((d) => d.id !== id);
      return filtered.map((d, i) => ({ ...d, dayNumber: i + 1 }));
    });
  }, []);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      {/* Builder Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-3 flex items-center gap-4 flex-shrink-0">
        <button
          onClick={() => navigate('/itinerary')}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer flex-shrink-0"
        >
          <i className="ri-arrow-left-line text-base" />
        </button>

        <div className="flex-1 min-w-0">
          {editingHeader ? (
            <div className="flex items-center gap-3 flex-wrap">
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-sm font-semibold text-gray-800 border-b border-gray-300 focus:outline-none focus:border-gray-500 bg-transparent min-w-[200px] pb-0.5"
                placeholder="Itinerary title..."
              />
              <input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="text-sm text-gray-500 border-b border-gray-200 focus:outline-none focus:border-gray-400 bg-transparent pb-0.5"
                placeholder="Destination..."
              />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-xs text-gray-400 border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-gray-400"
              />
              <span className="text-gray-300 text-xs">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-xs text-gray-400 border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-gray-400"
              />
              <div className="flex items-center gap-1 border border-gray-200 rounded-lg overflow-hidden">
                <button onClick={() => setPax(Math.max(1, pax - 1))} className="px-2 py-1 text-gray-400 hover:bg-gray-100 cursor-pointer text-sm">−</button>
                <span className="px-2 text-xs font-medium text-gray-600">{pax} pax</span>
                <button onClick={() => setPax(pax + 1)} className="px-2 py-1 text-gray-400 hover:bg-gray-100 cursor-pointer text-sm">+</button>
              </div>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none text-gray-500"
              >
                {['AED', 'USD', 'EUR', 'GBP', 'SAR', 'QAR'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  value={linkedBooking}
                  onChange={(e) => setLinkedBooking(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none text-gray-500 w-28"
                  placeholder="Booking # (optional)"
                />
                <input
                  value={linkedCustomer}
                  onChange={(e) => setLinkedCustomer(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none text-gray-500 w-36"
                  placeholder="Customer name (optional)"
                />
              </div>
              <button
                onClick={() => setEditingHeader(false)}
                className="text-xs px-3 py-1.5 rounded-lg text-white font-medium cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: brand }}
              >
                Done
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-800 truncate">{title || 'Untitled Itinerary'}</p>
                <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                  {destination && <span className="flex items-center gap-1"><i className="ri-map-pin-line" />{destination}</span>}
                  {nights > 0 && <span className="flex items-center gap-1"><i className="ri-moon-line" />{nights} nights</span>}
                  {startDate && <span>{startDate}</span>}
                  <span className="flex items-center gap-1"><i className="ri-user-line" />{pax} pax</span>
                  <span>{currency}</span>
                  {linkedCustomer && <span className="flex items-center gap-1"><i className="ri-account-circle-line" />{linkedCustomer}</span>}
                  {linkedBooking && <span className="px-1.5 py-0.5 bg-gray-100 rounded">{linkedBooking}</span>}
                </div>
              </div>
              <button
                onClick={() => setEditingHeader(true)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-300 cursor-pointer"
              >
                <i className="ri-edit-line text-sm" />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {saved && (
            <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg">
              <i className="ri-checkbox-circle-fill" />
              Saved
            </span>
          )}
          <button
            onClick={() => setShowPDF(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-pdf-line" />
            PDF Preview
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-save-line" />
            Save
          </button>
        </div>
      </div>

      {/* Builder Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Days Column */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {days.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${brand}12` }}>
                <i className="ri-map-2-line text-4xl" style={{ color: brand }} />
              </div>
              <h2 className="text-lg font-bold text-gray-700 mb-2">Start Building Your Itinerary</h2>
              <p className="text-sm text-gray-400 max-w-xs mb-6">
                Add days one by one, then fill each day with services like flights, hotels, tours, meals, and transfers.
              </p>
              <button
                onClick={addDay}
                className="flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: brand }}
              >
                <i className="ri-add-line" />
                Add Day 1
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={days.map((d) => d.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4 max-w-2xl">
                  {days.map((day) => (
                    <DayCard
                      key={day.id}
                      day={day}
                      onUpdate={updateDay}
                      onDelete={() => deleteDay(day.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {days.length > 0 && (
            <div className="max-w-2xl mt-4">
              <button
                onClick={addDay}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-200 text-sm text-gray-400 hover:border-gray-300 hover:text-gray-500 transition-colors cursor-pointer"
              >
                <i className="ri-add-circle-line text-lg" />
                Add Day {days.length + 1}
              </button>
            </div>
          )}
        </div>

        {/* Pricing Panel */}
        <div className="hidden lg:block overflow-y-auto px-4 py-5">
          <PricingSummary
            days={days}
            pax={pax}
            currency={currency}
            title={title}
            nights={nights}
            destination={destination}
            onExportPDF={() => setShowPDF(true)}
          />
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPDF && (
        <PDFPreview
          title={title}
          destination={destination}
          startDate={startDate}
          endDate={endDate}
          nights={nights}
          pax={pax}
          currency={currency}
          days={days}
          onClose={() => setShowPDF(false)}
        />
      )}
    </div>
  );
}
